import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../../api';
import { useCart } from '../../context/CartContext';

export default function Checkout() {
  const { cart, dispatch, total } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', telephone: '', rgpd_consent: false });
  const [type, setType] = useState('borne');
  const [table, setTable] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.telephone || !form.email) {
      setError('Le téléphone et l\'email sont obligatoires');
      return;
    }
    if (!form.rgpd_consent) {
      setError('Vous devez accepter la politique de confidentialité');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = {
        ...form,
        type_commande: type,
        reference_localisation: type === 'table' ? table : 'borne',
        items: cart.items.map(i => ({ product_id: i.product_id, quantite: i.quantite })),
      };
      const order = await api.createOrder(data);
      dispatch({ type: 'CLEAR' });
      navigate(`/tracking/${order.id}`);
    } catch (err) {
      setError('Erreur lors de la commande. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FEFCF8] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#8B7355] font-sans text-base">Votre panier est vide</p>
          <Link to="/order" className="text-[#C4673D] font-semibold text-sm mt-4 inline-block hover:underline">
            Retour aux stands
          </Link>
        </div>
      </div>
    );
  }

  const inputClasses = "w-full bg-white border border-[#E2D1B8] text-[#332A1C] rounded-xl px-4 py-3 placeholder-[#8B7355]/60 focus:outline-none focus:border-[#C4673D] focus:ring-2 focus:ring-[#C4673D]/20 transition";

  return (
    <div className="min-h-screen bg-[#FEFCF8]">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#F3DFC0] shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/cart" className="text-[#8B7355] hover:text-[#332A1C] transition">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </Link>
          <h1 className="font-serif text-[#332A1C] font-bold text-xl">Finaliser votre commande</h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Mode de commande */}
        <div>
          <label className="block font-serif text-[#332A1C] text-lg mb-3">Mode de commande</label>
          <div className="flex gap-3">
            <button type="button" onClick={() => setType('borne')}
              className={`flex-1 py-3 rounded-2xl font-sans font-medium text-sm border-2 transition-all duration-200 ${
                type === 'borne'
                  ? 'border-[#C4673D] bg-[#C4673D]/5 text-[#C4673D]'
                  : 'border-[#F3DFC0] bg-white text-[#8B7355] hover:border-[#E2D1B8]'
              }`}
            >Borne</button>
            <button type="button" onClick={() => setType('table')}
              className={`flex-1 py-3 rounded-2xl font-sans font-medium text-sm border-2 transition-all duration-200 ${
                type === 'table'
                  ? 'border-[#C4673D] bg-[#C4673D]/5 text-[#C4673D]'
                  : 'border-[#F3DFC0] bg-white text-[#8B7355] hover:border-[#E2D1B8]'
              }`}
            >Table</button>
          </div>
          {type === 'table' && (
            <input
              type="text" placeholder="Numéro de table" value={table}
              onChange={e => setTable(e.target.value)}
              className={`mt-3 ${inputClasses}`}
            />
          )}
        </div>

        {/* Coordonnées */}
        <div className="space-y-3">
          <h2 className="font-serif text-[#332A1C] text-lg">Vos coordonnées</h2>
          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="Prénom" value={form.prenom}
              onChange={e => setForm({ ...form, prenom: e.target.value })}
              className={inputClasses} />
            <input type="text" placeholder="Nom" value={form.nom}
              onChange={e => setForm({ ...form, nom: e.target.value })}
              className={inputClasses} />
          </div>
          <input type="tel" placeholder="Téléphone *" value={form.telephone} required
            onChange={e => setForm({ ...form, telephone: e.target.value })}
            className={inputClasses} />
          <input type="email" placeholder="Email *" value={form.email} required
            onChange={e => setForm({ ...form, email: e.target.value })}
            className={inputClasses} />
        </div>

        {/* RGPD */}
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative mt-0.5 flex-shrink-0">
            <input type="checkbox" checked={form.rgpd_consent}
              onChange={e => setForm({ ...form, rgpd_consent: e.target.checked })}
              className="sr-only peer" />
            <div className="h-5 w-5 rounded border-2 border-[#E2D1B8] bg-white peer-checked:bg-[#C4673D] peer-checked:border-[#C4673D] transition-all flex items-center justify-center peer-focus:ring-2 peer-focus:ring-[#C4673D]/20">
            </div>
            {form.rgpd_consent && (
              <svg className="absolute inset-0 h-5 w-5 text-white pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-sm text-[#8B7355] group-hover:text-[#332A1C] transition">
            J'accepte que mes données soient utilisées pour le suivi de commande et les communications des Halles de la Cartoucherie. *
          </span>
        </label>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded-2xl">
            {error}
          </div>
        )}

        {/* Récap + paiement */}
        <div className="bg-white rounded-2xl border border-[#F3DFC0] p-5 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <span className="font-serif text-[#8B7355] text-base">Total à payer</span>
            <span className="text-[#C4673D] font-bold text-2xl font-serif">{total.toFixed(2)} &euro;</span>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#C4673D] text-white font-semibold py-4 rounded-xl text-lg hover:bg-[#B05A33] transition-all duration-200 disabled:opacity-50 shadow-sm"
          >
            {loading ? 'Traitement...' : `Payer ${total.toFixed(2)} \u20AC`}
          </button>
          <p className="text-center text-xs text-[#8B7355] mt-3">Paiement simulé</p>
        </div>
      </form>
    </div>
  );
}
